import { Voucher } from '../types';
import { BillingContactPDFType } from '../../../components/Pdfs/PdfComponents/PdfBillingContactDetails';
import { getPdfAgentInfo } from '../../travelAgencyAccounts/helpers';
import { getPdfSupplierInfo } from '../../supplierAccounts/helpers';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import {
    getCurrencyDetails,
    getClientCurrencies,
    buildRateLine,
    buildReceiptColumns,
    buildReceiptRows,
    buildReceiptSummaryLine
} from './helpers';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';

/**
 * Build pdfInfo for Receipt / Transfer Receipt
 *
 * Matches the inline creation path in Receipt.tsx:
 * - Payment-method-specific columns/rows (Credit Card, Bank Transfer, Check, Cash)
 * - Rate line (simple text instead of old 3-column rate table)
 * - Credit card body box with Reference field
 * - "Equivalent: X USD" only on a LOCAL-currency receipt with "Print rate of
 *   exchange" ticked (on by default for bank transfers), matching TB's TRC. The
 *   stored info.printReceiptEquivalentAmount / ...Text columns stay ignored.
 * - isDeposited / isPaid flags
 */
export function buildReceiptPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;

    // ── Passenger (beneficiary) ─────────────────────────────────────────────
    const rawBeneficiary = info?.beneficiary;
    const beneficiaryRaw = Array.isArray(rawBeneficiary) ? rawBeneficiary[0] : rawBeneficiary;
    const rawPassenger = voucher.passenger;
    const fromVoucher = Array.isArray(rawPassenger) ? rawPassenger[0] : rawPassenger;
    const passengerSource =
        beneficiaryRaw ||
        (fromVoucher?.name || fromVoucher?.firstName ? fromVoucher : info?.selectedPassengerFlatten || null);
    // Strip numeric country ID — PdfPassengerDetails expects a country name string
    const passenger = passengerSource
        ? {
              ...passengerSource,
              country: typeof passengerSource.country === 'number' ? '' : passengerSource.country || ''
          }
        : null;

    // ── Currency ─────────────────────────────────────────────────────────────
    const { code: currencyCode, symbol: currencySymbol } = getCurrencyDetails(
        info?.currency,
        context.currencies
    );
    const { localCurrency, primaryCurrency } = getClientCurrencies(context.client, context.currencies);
    const localCurrencyCode = localCurrency?.code || '';

    const amount = Number(info?.amount) || 0;
    const paymentMethodType: string = info?.paymentMethodType || 'Payment';
    const paymentObj = info?.paymentMethodObject;

    // ── Payment-method-specific columns, rows, summary ───────────────────────
    const columns = buildReceiptColumns(paymentMethodType, currencyCode, paymentObj);
    const rows = buildReceiptRows(paymentMethodType, paymentObj, amount, currencyCode);

    // For a cheque receipt the total is the sum of the cheques. The form now derives
    // `info.amount` that way, but receipts issued before that fix stored 0 (or a
    // figure unrelated to the rows) and printed "TOTAL 0" above rows that did show
    // amounts — the rows fall back to each cheque's own amount, the summary had no
    // fallback. Recover the total from the cheques when the stored amount is empty.
    // Only when it is empty: a real stored amount is never second-guessed here.
    const summaryAmount = (() => {
        if (amount) return amount;
        if (paymentMethodType !== 'Check') return amount;
        const rawChecks = paymentObj?.checks;
        const checksArr = Array.isArray(rawChecks)
            ? rawChecks
            : rawChecks && typeof rawChecks === 'object'
              ? [rawChecks]
              : [];
        return checksArr.reduce((sum: number, check: any) => sum + (Number(check?.amount) || 0), 0);
    })();

    const summaryLine = buildReceiptSummaryLine(
        paymentMethodType,
        summaryAmount,
        currencyCode,
        currencySymbol
    );

    // ── Agent / Supplier info (can be on source or target side for receipts) ─
    let agentInfo: BillingContactPDFType | null = null;
    let supplierInfo: BillingContactPDFType | null = null;
    if ((voucher.sourceType === 'agent' || voucher.targetType === 'agent') && context.travelAgencyAccount) {
        agentInfo = getPdfAgentInfo(context.travelAgencyAccount);
    }
    if (context.supplierAccount) {
        supplierInfo = getPdfSupplierInfo(context.supplierAccount);
    }

    // ── Receiving bank text ──────────────────────────────────────────────────
    let receivingBankText = '';
    const rb = paymentObj?.receivingBank || paymentObj?.remittanceTransferringBank;
    if (rb) {
        const rbCurrency = rb.currency ? context.currencies.find((c) => c._id === rb.currency) : null;
        receivingBankText = [rb.name || rb.accountName || rb.bankName, rbCurrency?.code || currencyCode]
            .filter(Boolean)
            .join(' ');
    } else if (info?.accountName) {
        receivingBankText = info.accountName;
    }

    // ── Credit card body box (5 fields, matching inline) ─────────────────────
    const pdfBodyBoxData: Array<{ label: string; value: string }> = [];
    if (paymentMethodType === 'Credit Card') {
        pdfBodyBoxData.push(
            { label: 'Credit Company', value: paymentObj?.creditCompanyName || '' },
            { label: 'Card No.', value: paymentObj?.cardNo || '' },
            { label: 'Exp. Date', value: paymentObj?.expDate || '' },
            { label: 'Reference', value: paymentObj?.reference || '' }
        );
        // Supplier Id only applies to admin-entered receipts (where staff key
        // in the supplier reference manually). Skip it on customer-facing /
        // auto-generated receipts where there's no supplier-side identifier.
        if (paymentObj?.supplierId) {
            pdfBodyBoxData.push({ label: 'Supplier Id', value: paymentObj.supplierId });
        }
        // Which clearing terminal took the money. `paymentsTerminal` is the row
        // the agent picked on the receipt form (carried whole on the payment
        // object); `paymentsTerminalName` is the flat name the tour-registration
        // receipt stores. Either way the receipt names the terminal, so it can be
        // reconciled against that terminal's clearing report.
        const terminalName = paymentObj?.paymentsTerminal?.name || paymentObj?.paymentsTerminalName || '';
        if (terminalName) {
            pdfBodyBoxData.push({ label: 'Terminal', value: terminalName });
        }
    }

    // ── Rate line (only when printExchangeRate checkbox was checked) ────────
    const rate = Number(info?.rate) || 0;
    // A shekel receipt has no object↔local rate to print (it would be 1), so it
    // prints the primary-currency equivalent instead — the stored amountObj,
    // which is what the books hold, falling back to amount ÷ rate.
    const isLocalReceipt = !!localCurrencyCode && currencyCode === localCurrencyCode;
    const rateLine = !info?.printExchangeRate
        ? ''
        : isLocalReceipt
          ? primaryCurrency?.code && primaryCurrency.code !== localCurrencyCode
              ? buildEquivalentLine(amount, info?.amountObj, rate, primaryCurrency.code)
              : ''
          : buildRateLine(currencyCode, rate, localCurrencyCode);

    // ── isDeposited / isPaid ─────────────────────────────────────────────────
    const isDeposited = paymentMethodType === 'Credit Card' || paymentMethodType === 'Bank Transfer';
    const isPaid = isDeposited && info?.leftToPay != null ? amount >= Number(info.leftToPay) : false;

    // For agent receipts: agent is the "To:", passenger/beneficiary name goes in "For:"
    const isAgentReceipt = !!(agentInfo || voucher.sourceType === 'agent' || voucher.targetType === 'agent');
    const finalPassenger = isAgentReceipt ? null : passenger;
    const finalBottomDetails = isAgentReceipt ? passenger?.name || info?.for || '' : info?.for || '';

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        agentInfo,
        supplierInfo,
        passenger: finalPassenger,
        bottomDetails: finalBottomDetails,
        receivingBankText,
        paymentMethodObject: paymentObj || null,
        pdfBodyBoxData: pdfBodyBoxData.length > 0 ? pdfBodyBoxData : undefined,
        // New: simple rate text line instead of rate table
        rateLine,
        // Keep empty arrays for backward compatibility with PDF components that check rateTableRows?.length
        rateTableColumns: [],
        rateTableRows: [],
        // Deposit/payment status flags
        isDeposited,
        isPaid
    };
}

function buildEquivalentLine(
    amount: number,
    amountObj: unknown,
    rate: number,
    primaryCurrencyCode: string | undefined
): string {
    if (!primaryCurrencyCode) return '';
    const stored = Number(amountObj);
    const equivalent = stored > 0 ? stored : rate > 0 && rate !== 1 ? amount / rate : 0;
    if (!(equivalent > 0)) return '';
    return `Equivalent: ${taytanFormatNumberToString(equivalent)} ${primaryCurrencyCode}`;
}
